import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { ImageSlot } from './ImageSlot';
type LightboxProps = {
  images: string[];
  captions: string[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const navBtn = 'flex h-12 w-12 items-center justify-center rounded-full border border-teal-line text-white transition-colors duration-150 ease-out hover:border-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold';
export function Lightbox({
  images,
  captions,
  index,
  onClose,
  onChange
}: LightboxProps) {
  const reduce = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;
  const count = images.length;
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onChange((index + 1) % count);
      if (e.key === 'ArrowLeft') onChange((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onClose, onChange]);
  return <AnimatePresence data-id="element-101" data-mp="2hzj74oa3ykff">
      {index !== null && <motion.div role="dialog" aria-modal="true" aria-label={`Page preview: ${captions[index]}`} className="fixed inset-0 z-[60] flex flex-col bg-teal-dark/95 px-4 py-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onClick={onClose} data-id="element-102" data-mp="2dypjlw0kv7n2">
          <div className="flex items-center justify-between text-white" data-id="element-103" data-mp="z37flvjuto43">
            <p className="text-[14px] font-medium" data-id="element-104" data-mp="140psfx0zsnhd">
              <span className="font-display font-extrabold text-gold" data-id="element-105" data-mp="13zenhtvt99oo">
                {index + 1}/{count}
              </span>{' '}
              · {captions[index]}
            </p>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close preview" className="flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors duration-150 ease-out hover:bg-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold" data-id="element-106" data-mp="u1rxoccz3xpx">
              <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-107" data-mp="bdmvh9byskt8" />
            </button>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center py-4" data-id="element-108" data-mp="3tj8bmwt95flm">
            <motion.div key={index} className="aspect-[3/4] h-full max-h-[80vh] max-w-full overflow-hidden rounded-xl bg-white" initial={reduce ? false : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-109" data-mp="2b3wx1f7oucs">
              <ImageSlot url={images[index]} alt={captions[index]} slotName={`pageImages[${index}]`} fit="contain" data-id="element-110" data-mp="3jtja0ilp4yv9" />
            </motion.div>
          </div>

          <div className="flex items-center justify-center gap-4" onClick={e => e.stopPropagation()} data-id="element-111" data-mp="3swao6q5yi8ql">
            <button type="button" onClick={() => onChange((index - 1 + count) % count)} aria-label="Previous page" className={navBtn} data-id="element-112" data-mp="bfkd9eqwh9gp">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-113" data-mp="6gpta9h75ev0" />
            </button>
            <button type="button" onClick={() => onChange((index + 1) % count)} aria-label="Next page" className={navBtn} data-id="element-114" data-mp="kgiiu1gzlp1e">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-115" data-mp="fet86mxofddu" />
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}